import {
  Button,
  showNotification,
  UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField,
  UploadImage,
  type UploadImageValue,
} from 'platform/components';
import {HStack, makeStorageUrl, Show, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useRef, useState, type SyntheticEvent} from 'react';
import {flushSync} from 'react-dom';

import {isNil, isNotNil} from 'ramda';

import {
  useDetachCatalogueImageMutation,
  useUploadCatalogueImageMutation,
  type CatalogueImageOwner,
  type ImageAssetRef,
} from '@dms/api/new-vehicle';

import {useOnMount, type ApiError, type Nullish} from 'shared/web';

import {handleCatalogueError, isStaleCatalogueVersion, t} from '../utils/catalogueUi';

/** D49: the backend rejects larger files too; checking first spares the upload. */
const MAX_UPLOAD_MB = 5;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;
/**
 * PNG and JPEG signatures, the types UploadImage's input accepts (the backend also takes WebP). A
 * browser types a file by its extension, so a renamed text file still arrives as image/jpeg.
 */
const IMAGE_SIGNATURES = [
  [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  [0xff, 0xd8, 0xff],
];
const SIGNATURE_LENGTH = Math.max(...IMAGE_SIGNATURES.map(({length}) => length));
const hasImageSignature = (file: Blob) =>
  new Promise<boolean>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const head = reader.result instanceof ArrayBuffer ? new Uint8Array(reader.result) : null;
      resolve(
        IMAGE_SIGNATURES.some((signature) =>
          signature.every((byte, index) => head?.[index] === byte)
        )
      );
    };
    reader.onerror = () => resolve(true);
    reader.readAsArrayBuffer(file.slice(0, SIGNATURE_LENGTH));
  });
const PREVIEW_PX = 640;
/**
 * Preview of an image whose URL is unavailable, so Delete stays reachable. React drops an empty
 * src, which never errors and leaves Preview without actions; this fails without a request.
 */
const UNAVAILABLE_IMAGE_URL = 'data:,';

type CatalogueImageUploadProps = {
  value: UploadImageValue | null;
  isDisabled: boolean;
  upload: (file: File) => Promise<UploadImageValue>;
  remove: () => void;
  getIsWriting: () => boolean;
};

/** Shows the value the field gives it; an upload's own completion does not change it. */
function CatalogueImageUpload(props: CatalogueImageUploadProps) {
  const maxUploadFileSizeText = t(
    'entity.photo.labels.maxUploadFileSize',
    'Maximum upload file size: {max} MB',
    {max: MAX_UPLOAD_MB}
  );
  const formatNotAllowedText = t(
    'entity.watermark.notifications.imageFormatIsNotAllowed',
    "File cannot be upload to photos, because it's format is not allowed. Allowed formats are JPEG or PNG."
  );
  const [refusals, setRefusals] = useState(0);
  const refuse = (message: string) => {
    showNotification.error(message, {errorOrigin: 'business'});
    if (!props.getIsWriting()) {
      setRefusals((count) => count + 1);
    }
    return false;
  };
  return (
    <UploadImage
      key={refusals}
      value={props.value}
      isDisabled={props.isDisabled}
      uploadText={t('entity.newVehicle.catalogue.uploadImage', 'Upload image')}
      helperText={maxUploadFileSizeText}
      beforeUpload={async (file) => {
        if (file.size > MAX_UPLOAD_BYTES) {
          return refuse(maxUploadFileSizeText);
        }
        return (await hasImageSignature(file)) ? file : refuse(formatNotAllowedText);
      }}
      // The input's accept list refuses the file before beforeUpload; API errors arrive here too.
      onUploadError={(error) => {
        if (error instanceof UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField) {
          refuse(formatNotAllowedText);
        }
      }}
      onDeleteClick={props.remove}
      uploadFileRequest={props.upload}
    />
  );
}

export type CatalogueImageFieldProps = {
  owner: CatalogueImageOwner;
  ownerId: string;
  image: ImageAssetRef | Nullish;
  /**
   * The version of the record `image` comes from, so the field can tell an older read from its own
   * newer write. Not the version it writes with.
   */
  ownerVersion: number;
  /** The version the page holds for the owner; every image write sends it. */
  version: number;
  /**
   * The owner's version after this field's own successful write, with the id of the owner it
   * wrote. The page keeps it while it still shows that owner, so its next save does not resend a
   * version the image write moved on.
   */
  onVersionChange: (version: number, ownerId: string) => void;
  /**
   * Runs after a write was refused because another user changed the owner, once the reload
   * warning is shown. Pages pass what their own save does on that conflict; the version stays.
   */
  onStale?: () => void;
  /** Wired to the query `image` comes from, so an expired URL can be signed again. */
  onImageError: () => void;
  isDisabled?: boolean;
};

/**
 * The owner's image as one of its reads or this field's writes left it. Of two at one version the
 * later wins: it has the same image, and a refetch after an expired URL brings its new URL.
 */
type ImageSnapshot = {image: ImageAssetRef | null; version: number};
const newerSnapshot = (current: ImageSnapshot, next: ImageSnapshot) =>
  next.version >= current.version ? next : current;
/** A picked file shown from memory, for the asset its upload stored. */
type FilePreview = {assetReference: string; url: string};
type PendingWrite = {kind: 'upload'; preview: UploadImageValue} | {kind: 'remove'};
/** The preview id of a file whose upload has not answered yet. */
const UPLOADING_ID = 'uploading';

type ObjectUrlLeaseProps = {url: string};

/** Keeps a picked file's object URL while it may show; unmounting releases it. */
function ObjectUrlLease(props: ObjectUrlLeaseProps) {
  const isHeld = useRef(false);
  useOnMount(
    () => {
      isHeld.current = true;
    },
    () => {
      isHeld.current = false;
      // Strict Mode runs this cleanup and then the setup again on the same instance before the
      // microtask; only a lease whose setup did not run again releases.
      queueMicrotask(() => {
        if (!isHeld.current) {
          URL.revokeObjectURL(props.url);
        }
      });
    }
  );
  return null;
}

/**
 * The optional image of a catalogue item. Upload, replace and remove save at once, apart from the
 * item's own form; removing only detaches the stored file.
 */
export function CatalogueImageField(props: CatalogueImageFieldProps) {
  // Keyed by its owner: a page that moves to another item drops what the field knew of the
  // previous one, and a late answer for it cannot reach the next.
  return <OwnerImageField key={`${props.owner}|${props.ownerId}`} {...props} />;
}

function OwnerImageField(props: CatalogueImageFieldProps) {
  const [uploadImage] = useUploadCatalogueImageMutation();
  const [detachImage] = useDetachCatalogueImageMutation();
  // The newest image a read of the owner or this field's write brought, whichever answers first. A
  // later read of an older version, such as a list that no longer has the owner, cannot undo it.
  const [snapshot, setSnapshot] = useState<ImageSnapshot>({
    image: props.image ?? null,
    version: props.ownerVersion,
  });
  const current = newerSnapshot(snapshot, {
    image: props.image ?? null,
    version: props.ownerVersion,
  });
  if (current.version > snapshot.version) {
    setSnapshot(current);
  }
  const {image} = current;
  const storedUrl = isNil(image?.url)
    ? null
    : makeStorageUrl(image.url, PREVIEW_PX, PREVIEW_PX, 'contain');
  // A stored URL whose image failed shows nothing, so the picked file shows instead.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const previewUrl = storedUrl === failedUrl ? null : storedUrl;
  const [pending, setPending] = useState<PendingWrite | null>(null);
  // Replace opens the upload zone for this image only; any other image ends it.
  const [replacedAsset, setReplacedAsset] = useState<string | null>(null);
  const isReplacing = !isNil(image) && replacedAsset === image.assetReference;
  // The uploaded file stays on show instead of a stored URL that fails.
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null);
  // Once a newer image replaced or removed the uploaded one, its picked file is never shown again.
  const retainedPreview =
    filePreview?.assetReference === image?.assetReference ? filePreview : null;
  // Each picked file's URL lives while it is the running upload or the image's retained file.
  const leasedUrls = [
    pending?.kind === 'upload' ? pending.preview.url : null,
    retainedPreview?.url,
  ].filter(isNotNil);
  const isWriting = useRef(false);
  const writeAlone = <T,>(write: () => Promise<T>) => {
    if (isWriting.current) {
      return Promise.reject(new Error('Another image write is in flight'));
    }
    isWriting.current = true;
    return write().finally(() => {
      isWriting.current = false;
    });
  };
  const rethrowHandled = (error: ApiError) => {
    handleCatalogueError(error);
    if (isStaleCatalogueVersion(error)) {
      props.onStale?.();
    }
    throw error;
  };
  const upload = (file: File) =>
    writeAlone(() => {
      const url = URL.createObjectURL(file);
      const replaced = replacedAsset;
      // The file's lease mounts before the write can answer, so even an instant failure releases it.
      flushSync(() => {
        setPending({kind: 'upload', preview: {id: UPLOADING_ID, url}});
        setReplacedAsset(null);
      });
      return uploadImage({
        owner: props.owner,
        ownerId: props.ownerId,
        body: {file, version: props.version},
      })
        .unwrap()
        .then(({imageAssetRef: uploaded, version}) => {
          props.onVersionChange(version, props.ownerId);
          setSnapshot((latest) => newerSnapshot(latest, {image: uploaded, version}));
          setFilePreview({assetReference: uploaded.assetReference, url});
          setPending(null);
          return {id: uploaded.assetReference, url};
        })
        .catch((error: ApiError) => {
          setPending(null);
          setReplacedAsset(replaced);
          return rethrowHandled(error);
        });
    });
  const remove = () =>
    void writeAlone(() => {
      setPending({kind: 'remove'});
      return detachImage({
        owner: props.owner,
        ownerId: props.ownerId,
        versionedWriteRequestBody: {version: props.version},
      })
        .unwrap()
        .then(({version}) => {
          props.onVersionChange(version, props.ownerId);
          setSnapshot((latest) => newerSnapshot(latest, {image: null, version}));
          setFilePreview(null);
          setPending(null);
        })
        .catch((error: ApiError) => {
          setPending(null);
          return rethrowHandled(error);
        });
    }).catch(() => undefined);
  const value = match({pending, isReplacing, image})
    .with({pending: {kind: 'upload'}}, ({pending: uploading}) => uploading.preview)
    .with({isReplacing: true}, () => null)
    .with({image: Pattern.nonNullable}, ({image: stored}) => ({
      id: stored.assetReference,
      url: previewUrl ?? retainedPreview?.url ?? UNAVAILABLE_IMAGE_URL,
    }))
    .otherwise(() => null);
  // Only the stored image's own URL can be signed again; a file preview or the placeholder cannot.
  const isPreviewUrl = (event: SyntheticEvent) =>
    !isNil(previewUrl) &&
    event.target instanceof HTMLImageElement &&
    event.target.getAttribute('src') === previewUrl;
  const isBusy = !isNil(pending);
  return (
    <VStack spacing={2}>
      {/* React propagates img errors, so an expired preview URL reaches the refetch. */}
      <div
        onError={(event) => {
          if (isPreviewUrl(event)) {
            setFailedUrl(previewUrl);
            props.onImageError();
          }
        }}
      >
        <CatalogueImageUpload
          value={value}
          isDisabled={Boolean(props.isDisabled) || isBusy}
          upload={upload}
          remove={remove}
          getIsWriting={() => isWriting.current}
        />
        {leasedUrls.map((url) => (
          <ObjectUrlLease key={url} url={url} />
        ))}
      </div>
      <Show when={!props.isDisabled && !isNil(image)}>
        <HStack spacing={4}>
          <Button
            variant="link"
            isDisabled={isBusy}
            title={
              isReplacing
                ? t('general.actions.cancel', 'Cancel')
                : t('entity.newVehicle.catalogue.replaceImage', 'Replace image')
            }
            onClick={() => setReplacedAsset(isReplacing ? null : (image?.assetReference ?? null))}
          />
          {/* The preview's own Delete is an unnamed icon that shows only on hover. */}
          <Button
            variant="link"
            isDisabled={isBusy}
            title={t('entity.newVehicle.catalogue.removeImage', 'Remove image')}
            onClick={remove}
          />
        </HStack>
      </Show>
    </VStack>
  );
}
